import { useEffect, useState, useCallback } from "react";
import { UsersRound, KeyRound, ShieldOff, ShieldCheck, ScrollText, Copy, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo, fmtDate, formatApiErrorDetail } from "../lib/api";

function KeyReveal({ reveal, onDismiss }) {
  if (!reveal) return null;
  return (
    <div data-testid="advisor-key-reveal" className="border border-amber-500/50 bg-amber-500/5 px-5 py-4">
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-amber-300">
        Clé de {reveal.name} — affichée une seule fois
      </div>
      <div className="mt-2 flex items-center gap-3">
        <code data-testid="advisor-key-value" className="break-all font-mono text-sm text-white">{reveal.key}</code>
        <button data-testid="advisor-key-copy-btn"
          onClick={() => { navigator.clipboard.writeText(reveal.key); toast.success("Clé copiée"); }}
          className="shrink-0 border border-border px-2 py-1 text-neutral-400 hover:text-white">
          <Copy size={14} />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono text-[11px] text-amber-200/70">{reveal.warning}</span>
        <button data-testid="advisor-key-dismiss-btn" onClick={onDismiss}
          className="font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-400 hover:text-white">
          J'ai conservé la clé — masquer
        </button>
      </div>
    </div>
  );
}

function CreateForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ advisor_id: "", name: "", role: "Architecte-conseil de Mario" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/advisors", form);
      toast.success(`Conseiller ${data.advisor.name} créé`);
      setOpen(false);
      setForm({ advisor_id: "", name: "", role: "Architecte-conseil de Mario" });
      onCreated({ key: data.api_key, warning: data.warning, name: data.advisor.name });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!open)
    return (
      <button data-testid="advisor-create-open-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 border border-border bg-card px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-neutral-300 hover:border-cyan-500/50 hover:text-white">
        <Plus size={14} /> Ajouter un conseiller
      </button>
    );

  return (
    <form onSubmit={submit} data-testid="advisor-create-form" className="space-y-3 border border-border bg-card px-5 py-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {[["advisor_id", "identifiant (ex. lea)"], ["name", "nom (ex. Léa)"], ["role", "rôle"]].map(([k, ph]) => (
          <input key={k} data-testid={`advisor-form-${k}`} required value={form[k]} placeholder={ph}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="border border-border bg-background px-3 py-2 font-mono text-sm text-white placeholder:text-neutral-600 focus:border-cyan-500/50 focus:outline-none" />
        ))}
      </div>
      <div className="flex gap-2">
        <button data-testid="advisor-form-submit-btn" type="submit" disabled={busy}
          className="border border-cyan-500/50 bg-cyan-500/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50">
          {busy ? "Création…" : "Créer + générer la clé"}
        </button>
        <button data-testid="advisor-form-cancel-btn" type="button" onClick={() => setOpen(false)}
          className="border border-border px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-neutral-500 hover:text-white">
          Annuler
        </button>
      </div>
    </form>
  );
}

function Journal({ aid }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/advisors/${aid}/journal`).then(({ data }) => {
      if (!alive) return;
      setData(data);
      setSelected((s) => s || data.sessions[0]?.id || null);
    }).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [aid]);

  if (!data) return <div className="px-5 py-4 font-mono text-xs text-neutral-600">Chargement du journal…</div>;

  const msgs = data.messages.filter((m) => m.session_id === selected).sort((a, b) => a.created_at.localeCompare(b.created_at));

  return (
    <div data-testid="advisor-journal" className="space-y-4 border-t border-border px-5 py-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          Conversations {data.advisor.name} ↔ NEXUS ({data.sessions.length} session{data.sessions.length > 1 ? "s" : ""})
        </div>
        {data.sessions.length === 0 && <div className="mt-2 font-mono text-xs text-neutral-600">Aucune conversation</div>}
        {data.sessions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {data.sessions.map((s) => (
              <button key={s.id} data-testid={`advisor-session-${s.id}`} onClick={() => setSelected(s.id)}
                className={`border px-3 py-1.5 text-left font-mono text-[11px] ${
                  selected === s.id ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-300" : "border-border text-neutral-500 hover:text-neutral-300"}`}>
                {s.title || "Sans titre"} <span className="ml-1 opacity-60">· {timeAgo(s.updated_at)}</span>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <div data-testid="advisor-chat-view" className="mt-3 max-h-[28rem] space-y-3 overflow-y-auto border border-border bg-background p-4">
            {msgs.length === 0 && <div className="font-mono text-xs text-neutral-600">Aucun message dans cette session</div>}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "advisor" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[85%] border px-4 py-3 ${
                  m.role === "advisor" ? "border-cyan-500/30 bg-cyan-500/5" : "border-border bg-card"}`}>
                  <div className="flex items-baseline gap-3 font-mono text-[10px] uppercase tracking-[0.15em]">
                    <span className={m.role === "advisor" ? "text-cyan-400" : "text-emerald-400"}>
                      {m.role === "advisor" ? data.advisor.name : "NEXUS"}
                    </span>
                    <span className="text-neutral-600">{fmtDate(m.created_at)}</span>
                    {m.sources?.length > 0 && <span className="text-neutral-600">{m.sources.length} sources</span>}
                    {m.order_id && <span className="text-amber-300">→ ordre {m.order_id.slice(0, 8)}</span>}
                  </div>
                  <div className="mt-1.5 whitespace-pre-wrap text-[15px] leading-relaxed text-neutral-200">{m.content}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Propositions ({data.proposals.length})</div>
        <div className="mt-2 max-h-64 space-y-2 overflow-y-auto">
          {data.proposals.length === 0 && <div className="font-mono text-xs text-neutral-600">Aucune proposition</div>}
          {data.proposals.map((p) => (
            <div key={p.id} className="border border-border bg-background px-3 py-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                [{p.kind}] {fmtDate(p.created_at)} · statut {p.status}
                {p.order_id && <span className="ml-2 text-amber-300">→ ordre {p.order_id.slice(0, 8)}</span>}
              </div>
              <div className="mt-1 text-sm font-medium text-white">{p.title}</div>
              <div className="mt-1 text-sm text-neutral-400">{p.content}</div>
              {p.assessment && (
                <div className="mt-2 border-l-2 border-cyan-500/40 pl-3 text-sm text-neutral-300">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-400">
                    Verdict NEXUS (confiance {p.assessment.confidence})
                  </span>
                  <div className="mt-0.5">{p.assessment.text}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Presence({ a }) {
  if (!a.active) return null;
  const mins = a.last_seen_at ? (Date.now() - new Date(a.last_seen_at).getTime()) / 60000 : null;
  const [color, pulse, label] =
    mins === null ? ["bg-neutral-600", "", "Jamais connecté"]
    : mins < 5 ? ["bg-emerald-400", "animate-pulse", "En ligne"]
    : mins < 60 ? ["bg-amber-400", "", "Actif récemment"]
    : ["bg-neutral-500", "", "Inactif"];
  return (
    <span data-testid={`advisor-presence-${a.advisor_id}`}
      className="ml-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400">
      <span className={`h-2 w-2 rounded-full ${color} ${pulse}`} />
      {label}
    </span>
  );
}

export default function Advisors() {
  const [advisors, setAdvisors] = useState([]);
  const [reveal, setReveal] = useState(null);
  const [journalFor, setJournalFor] = useState(null);

  const load = useCallback(() => {
    api.get("/advisors").then(({ data }) => setAdvisors(data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const regenerate = async (a) => {
    if (!window.confirm(`Régénérer la clé de ${a.name} ? L'ancienne clé sera immédiatement révoquée et ${a.name} perdra l'accès jusqu'à réception de la nouvelle clé.`)) return;
    try {
      const { data } = await api.post(`/advisors/${a.advisor_id}/keys/regenerate`);
      setReveal({ key: data.api_key, warning: data.warning, name: a.name });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const revoke = async (a) => {
    if (!window.confirm(`Révoquer TOUT l'accès de ${a.name} ? Son identité et ses clés seront désactivées immédiatement.`)) return;
    try { await api.post(`/advisors/${a.advisor_id}/revoke`); toast.success(`${a.name} révoqué`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const reactivate = async (a) => {
    try {
      await api.post(`/advisors/${a.advisor_id}/reactivate`);
      toast.success(`${a.name} réactivé — régénérez une clé pour rétablir l'accès`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="advisors-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Conseillers</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            Identités externes de confiance — consultation, dialogue et proposition uniquement. Aucune signature, aucun ordre.
          </p>
        </div>
        <CreateForm onCreated={(r) => { setReveal(r); load(); }} />
      </div>

      <KeyReveal reveal={reveal} onDismiss={() => setReveal(null)} />

      <div className="space-y-3">
        {advisors.length === 0 && (
          <div className="border border-border bg-card px-5 py-10 text-center font-mono text-xs text-neutral-600">
            Aucun conseiller enregistré
          </div>
        )}
        {advisors.map((a) => (
          <div key={a.advisor_id} data-testid={`advisor-card-${a.advisor_id}`} className="border border-white/10 bg-card shadow-[0_0_16px_rgba(0,229,255,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="flex items-center gap-4">
                <UsersRound size={18} className={a.active ? "text-cyan-400" : "text-neutral-600"} />
                <div>
                  <div className="font-mono text-sm text-white">
                    {a.name} <span className="text-neutral-500">({a.advisor_id})</span>
                    <span data-testid={`advisor-status-${a.advisor_id}`}
                      className={`ml-3 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] ${
                        a.active ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
                      {a.active ? "Actif" : "Révoqué"}
                    </span>
                    <Presence a={a} />
                  </div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600">
                    {a.role} · type {a.type} · clé {a.key_prefix || "aucune"} · dernier contact {timeAgo(a.last_seen_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`advisor-journal-btn-${a.advisor_id}`}
                  onClick={() => setJournalFor(journalFor === a.advisor_id ? null : a.advisor_id)}
                  className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-400 hover:text-white">
                  <ScrollText size={13} /> Journal
                </button>
                {a.active && (
                  <>
                    <button data-testid={`advisor-regen-btn-${a.advisor_id}`} onClick={() => regenerate(a)}
                      className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-amber-300/80 hover:text-amber-300">
                      <KeyRound size={13} /> Régénérer la clé
                    </button>
                    <button data-testid={`advisor-revoke-btn-${a.advisor_id}`} onClick={() => revoke(a)}
                      className="flex items-center gap-1.5 border border-red-500/30 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-red-400/80 hover:text-red-400">
                      <ShieldOff size={13} /> Révoquer
                    </button>
                  </>
                )}
                {!a.active && (
                  <button data-testid={`advisor-reactivate-btn-${a.advisor_id}`} onClick={() => reactivate(a)}
                    className="flex items-center gap-1.5 border border-emerald-500/30 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-400/80 hover:text-emerald-400">
                    <ShieldCheck size={13} /> Réactiver
                  </button>
                )}
              </div>
            </div>
            {journalFor === a.advisor_id && <Journal aid={a.advisor_id} />}
          </div>
        ))}
      </div>
    </div>
  );
}
